/**
 * Boot hook for a BROWSER-DRIVEN acceptance run — UI tests carried out in a
 * person's own Chrome, started by Claude Code (`ui_test.py browser-run`).
 *
 * The run's start link carries the run's one-time page key in its FRAGMENT
 * (`#tw-test=…`, never sent to a server). This hook, called before the app
 * renders, moves it into this tab's sessionStorage, strips it from the
 * address, and loads the runner script from the backend
 * (`/api/ui-test-browser/runner.js`, backend/app/services/ui_testing/
 * browser_runner.js), which then carries out the server driver's operations.
 * The app renders only once the runner is in place, so it sees the page from
 * its first request — as the server's own runner does with an init script.
 *
 * In every other tab this is one sessionStorage read and nothing else.
 * Plan: docs/plans/browser-driven-acceptance-testing.md.
 */

import { APP_BASE } from '@/config/basePath'

const KEY = 'tw-test-run'
const FRAGMENT = /(?:^#|&)tw-test=([A-Za-z0-9_-]+)/

interface TestRunKey {
  o: string
  p: string
  r: string
  t: string
  pending?: number
  after?: number
}

declare global {
  interface Window {
    __TWASTA_SHELL_LAYOUT__?: string
    __TW_TEST_API_BASE__?: string
    __twTestCapture?: (o: { fullPage?: boolean; type?: 'png' | 'jpeg'; quality?: number }) => Promise<Blob | null>
  }
}

function fromFragment(): TestRunKey | null {
  const m = typeof location !== 'undefined' ? location.hash.match(FRAGMENT) : null
  if (!m) return null
  let key: TestRunKey | null = null
  try {
    const b64 = m[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(escape(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))))
    const v = JSON.parse(json)
    if (v && typeof v === 'object' && v.r && v.t && v.o && v.p) key = v as TestRunKey
  } catch {
    key = null
  }
  // Off the address either way: the key must not linger in the location bar
  // or be copied along with the link.
  try {
    history.replaceState(history.state, '', location.pathname + location.search)
  } catch { /* an old browser — harmless */ }
  return key
}

function stored(): TestRunKey | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || 'null')
    return v && v.r && v.t ? (v as TestRunKey) : null
  } catch {
    return null
  }
}

/** A promise that settles once the runner is loaded, or null when this tab
 *  is not part of a run. Never rejects: a run that cannot start must not
 *  stop the application from rendering. */
export function bootBrowserTest(): Promise<void> | null {
  let key: TestRunKey | null = null
  try {
    const fresh = fromFragment()
    if (fresh) {
      // A new start link replaces whatever run this tab took part in before.
      sessionStorage.setItem(KEY, JSON.stringify(fresh))
      key = fresh
    } else {
      key = stored()
    }
  } catch {
    key = null
  }
  if (!key) return null

  // The cases drive the classic menu tree, whatever layout the person picked.
  window.__TWASTA_SHELL_LAYOUT__ = 'classic'
  window.__TW_TEST_API_BASE__ = `${APP_BASE}/api/ui-test-browser`
  window.__twTestCapture = async (o) => {
    const { captureScreenshot } = await import('@twasta/page-runtime/support')
    return captureScreenshot(null, o)
  }
  return new Promise<void>((resolve) => {
    const done = () => resolve()
    const s = document.createElement('script')
    s.src = `${APP_BASE}/api/ui-test-browser/runner.js`
    s.async = false
    s.onload = done
    s.onerror = () => {
      console.warn('[ui-test] the test runner could not be loaded — this tab will not take part in the run')
      done()
    }
    document.head.appendChild(s)
    // Never hold the application back for long.
    window.setTimeout(done, 8000)
  })
}
